import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderInitials: string;
  text: string;
  timestamp: Date;
  isRead: boolean;
}

export interface Chat {
  id: string;
  participantId: string;
  participantName: string;
  participantInitials: string;
  participantAvatar?: string;
  lastMessage: string;
  lastMessageTime: Date;
  unreadCount: number;
  messages: ChatMessage[];
}

@Injectable({
  providedIn: 'root',
})
export class MockChatService {
  private mockChats: Chat[] = [
    {
      id: 'chat-1',
      participantId: 'user-1',
      participantName: 'Sarah Johnson',
      participantInitials: 'SJ',
      lastMessage: 'Thanks for the information! I\'ll get back to you soon.',
      lastMessageTime: new Date('2025-01-01T14:30:00'),
      unreadCount: 2,
      messages: [
        {
          id: 'msg-1',
          senderId: 'user-1',
          senderName: 'Sarah Johnson',
          senderInitials: 'SJ',
          text: 'Hi! I saw your post about the software engineer position. I\'m very interested!',
          timestamp: new Date('2025-01-01T10:00:00'),
          isRead: true,
        },
        {
          id: 'msg-2',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'Great! I\'d be happy to discuss the role with you. What questions do you have?',
          timestamp: new Date('2025-01-01T10:15:00'),
          isRead: true,
        },
        {
          id: 'msg-3',
          senderId: 'user-1',
          senderName: 'Sarah Johnson',
          senderInitials: 'SJ',
          text: 'What are the main technologies used in this role?',
          timestamp: new Date('2025-01-01T11:00:00'),
          isRead: true,
        },
        {
          id: 'msg-4',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'We primarily use React, TypeScript, and Node.js. We also work with AWS for cloud infrastructure.',
          timestamp: new Date('2025-01-01T11:30:00'),
          isRead: true,
        },
        {
          id: 'msg-5',
          senderId: 'user-1',
          senderName: 'Sarah Johnson',
          senderInitials: 'SJ',
          text: 'Perfect! I have experience with all of those. What\'s the team structure like?',
          timestamp: new Date('2025-01-01T12:00:00'),
          isRead: true,
        },
        {
          id: 'msg-6',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'We have a small, collaborative team of 8 developers. We work in an agile environment with daily standups.',
          timestamp: new Date('2025-01-01T13:00:00'),
          isRead: true,
        },
        {
          id: 'msg-7',
          senderId: 'user-1',
          senderName: 'Sarah Johnson',
          senderInitials: 'SJ',
          text: 'That sounds great! I\'d love to learn more about the company culture.',
          timestamp: new Date('2025-01-01T14:00:00'),
          isRead: false,
        },
        {
          id: 'msg-8',
          senderId: 'user-1',
          senderName: 'Sarah Johnson',
          senderInitials: 'SJ',
          text: 'Thanks for the information! I\'ll get back to you soon.',
          timestamp: new Date('2025-01-01T14:30:00'),
          isRead: false,
        },
      ],
    },
    {
      id: 'chat-2',
      participantId: 'user-2',
      participantName: 'Mike Chen',
      participantInitials: 'MC',
      lastMessage: 'Looking forward to it!',
      lastMessageTime: new Date('2025-01-01T13:45:00'),
      unreadCount: 0,
      messages: [
        {
          id: 'msg-9',
          senderId: 'user-2',
          senderName: 'Mike Chen',
          senderInitials: 'MC',
          text: 'Hello! I saw your job posting for the DevOps Engineer position.',
          timestamp: new Date('2025-01-01T09:00:00'),
          isRead: true,
        },
        {
          id: 'msg-10',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'Hi Mike! Thanks for your interest. What would you like to know?',
          timestamp: new Date('2025-01-01T09:15:00'),
          isRead: true,
        },
        {
          id: 'msg-11',
          senderId: 'user-2',
          senderName: 'Mike Chen',
          senderInitials: 'MC',
          text: 'I\'m curious about the tech stack and infrastructure setup.',
          timestamp: new Date('2025-01-01T10:00:00'),
          isRead: true,
        },
        {
          id: 'msg-12',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'We use Kubernetes, Docker, and primarily AWS. We also use Terraform for infrastructure as code.',
          timestamp: new Date('2025-01-01T10:30:00'),
          isRead: true,
        },
        {
          id: 'msg-13',
          senderId: 'user-2',
          senderName: 'Mike Chen',
          senderInitials: 'MC',
          text: 'Excellent! I have extensive experience with all of those. When can we schedule an interview?',
          timestamp: new Date('2025-01-01T12:00:00'),
          isRead: true,
        },
        {
          id: 'msg-14',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'How about next Tuesday at 2 PM?',
          timestamp: new Date('2025-01-01T13:00:00'),
          isRead: true,
        },
        {
          id: 'msg-15',
          senderId: 'user-2',
          senderName: 'Mike Chen',
          senderInitials: 'MC',
          text: 'Looking forward to it!',
          timestamp: new Date('2025-01-01T13:45:00'),
          isRead: true,
        },
      ],
    },
    {
      id: 'chat-3',
      participantId: 'user-3',
      participantName: 'Emily Davis',
      participantInitials: 'ED',
      lastMessage: 'Can you send me more details about the benefits package?',
      lastMessageTime: new Date('2025-01-01T12:30:00'),
      unreadCount: 1,
      messages: [
        {
          id: 'msg-16',
          senderId: 'user-3',
          senderName: 'Emily Davis',
          senderInitials: 'ED',
          text: 'Hi! I\'m interested in the Frontend Developer position.',
          timestamp: new Date('2025-01-01T08:00:00'),
          isRead: true,
        },
        {
          id: 'msg-17',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'Hi Emily! Great to hear from you. What questions do you have?',
          timestamp: new Date('2025-01-01T08:15:00'),
          isRead: true,
        },
        {
          id: 'msg-18',
          senderId: 'user-3',
          senderName: 'Emily Davis',
          senderInitials: 'ED',
          text: 'I\'d like to know more about the remote work policy.',
          timestamp: new Date('2025-01-01T09:00:00'),
          isRead: true,
        },
        {
          id: 'msg-19',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'We offer fully remote work with flexible hours. We also have optional co-working spaces in major cities.',
          timestamp: new Date('2025-01-01T09:30:00'),
          isRead: true,
        },
        {
          id: 'msg-20',
          senderId: 'user-3',
          senderName: 'Emily Davis',
          senderInitials: 'ED',
          text: 'That\'s perfect! Can you send me more details about the benefits package?',
          timestamp: new Date('2025-01-01T12:30:00'),
          isRead: false,
        },
      ],
    },
    {
      id: 'chat-4',
      participantId: 'user-4',
      participantName: 'David Wilson',
      participantInitials: 'DW',
      lastMessage: 'I\'ll review it and get back to you by end of week.',
      lastMessageTime: new Date('2025-01-01T11:00:00'),
      unreadCount: 0,
      messages: [
        {
          id: 'msg-21',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'Hi David! I saw your application for the Project Manager role.',
          timestamp: new Date('2025-01-01T08:00:00'),
          isRead: true,
        },
        {
          id: 'msg-22',
          senderId: 'user-4',
          senderName: 'David Wilson',
          senderInitials: 'DW',
          text: 'Hello! Yes, I\'m very interested in the position.',
          timestamp: new Date('2025-01-01T08:30:00'),
          isRead: true,
        },
        {
          id: 'msg-23',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'Great! I\'ve sent you the detailed job description via email.',
          timestamp: new Date('2025-01-01T09:00:00'),
          isRead: true,
        },
        {
          id: 'msg-24',
          senderId: 'user-4',
          senderName: 'David Wilson',
          senderInitials: 'DW',
          text: 'I\'ll review it and get back to you by end of week.',
          timestamp: new Date('2025-01-01T11:00:00'),
          isRead: true,
        },
      ],
    },
    {
      id: 'chat-5',
      participantId: 'user-5',
      participantName: 'Lisa Anderson',
      participantInitials: 'LA',
      lastMessage: 'Thank you so much for your help!',
      lastMessageTime: new Date('2024-12-31T16:00:00'),
      unreadCount: 0,
      messages: [
        {
          id: 'msg-25',
          senderId: 'user-5',
          senderName: 'Lisa Anderson',
          senderInitials: 'LA',
          text: 'Hi! I have a question about the application process.',
          timestamp: new Date('2024-12-31T14:00:00'),
          isRead: true,
        },
        {
          id: 'msg-26',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'Of course! I\'d be happy to help. What\'s your question?',
          timestamp: new Date('2024-12-31T14:15:00'),
          isRead: true,
        },
        {
          id: 'msg-27',
          senderId: 'user-5',
          senderName: 'Lisa Anderson',
          senderInitials: 'LA',
          text: 'How long does the interview process typically take?',
          timestamp: new Date('2024-12-31T15:00:00'),
          isRead: true,
        },
        {
          id: 'msg-28',
          senderId: 'current-user',
          senderName: 'You',
          senderInitials: 'U',
          text: 'Usually 2-3 weeks from initial interview to offer. We try to move quickly for great candidates!',
          timestamp: new Date('2024-12-31T15:30:00'),
          isRead: true,
        },
        {
          id: 'msg-29',
          senderId: 'user-5',
          senderName: 'Lisa Anderson',
          senderInitials: 'LA',
          text: 'Thank you so much for your help!',
          timestamp: new Date('2024-12-31T16:00:00'),
          isRead: true,
        },
      ],
    },
  ];

  getChats(): Observable<Chat[]> {
    return of(this.mockChats).pipe(delay(200));
  }

  getChat(id: string): Observable<Chat | undefined> {
    const chat = this.mockChats.find((c) => c.id === id);
    return of(chat).pipe(delay(200));
  }

  sendMessage(chatId: string, text: string): Observable<ChatMessage> {
    const chat = this.mockChats.find((c) => c.id === chatId);
    if (!chat) {
      throw new Error('Chat not found');
    }

    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      senderId: 'current-user',
      senderName: 'You',
      senderInitials: 'U',
      text: text.trim(),
      timestamp: new Date(),
      isRead: false,
    };

    chat.messages.push(newMessage);
    chat.lastMessage = newMessage.text;
    chat.lastMessageTime = newMessage.timestamp;

    return of(newMessage).pipe(delay(100));
  }
}

